import { useEffect, useState } from "react";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { ProductCard } from "@/components/ProductCard";
import { useOptOutPopupData } from "@/hooks/useOptOutPopupData";
import { useOptOutPopupStore } from "@/store/useOptOutPopupStore";
import { appendOrderId } from "@/utils/url";
import { buildUpsellPayload, postUpsell } from "@/utils/upsell";

function SubmitArrow() {
  return (
    <svg
      fill="none"
      height="16"
      viewBox="0 0 15 16"
      width="15"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        clipRule="evenodd"
        d="M0.535421 15.1812C0.78087 15.4271 1.1031 15.55 1.42517 15.55C1.74732 15.55 2.06948 15.4281 2.31532 15.1803L8.60888 8.88668C9.10057 8.39501 9.10057 7.59848 8.60888 7.10679L2.31532 0.813223C1.82363 0.321536 1.02711 0.321536 0.535421 0.813223C0.0437348 1.30491 0.0437348 2.10144 0.535421 2.59313L5.94081 7.99773L0.535421 13.4013C0.0437348 13.893 0.0437348 14.6896 0.535421 15.1812ZM6.19963 15.1812C6.44508 15.4271 6.7673 15.55 7.08939 15.55C7.41152 15.55 7.73368 15.4281 7.97952 15.1803L14.2731 8.88668C14.7648 8.39501 14.7648 7.59848 14.2731 7.10679L7.97952 0.813223C7.48784 0.321536 6.69132 0.321536 6.19963 0.813223C5.70795 1.30491 5.70795 2.10144 6.19963 2.59313L11.605 7.99773L6.19963 13.4013C5.70795 13.893 5.70795 14.6896 6.19963 15.1812Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
}

export default function App() {
  const { data, error, isLoading, refetch } = useOptOutPopupData();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    data: popupData,
    getSelectedProducts,
    hydrate,
    isSubmitting,
    selectedProductIds,
    setSubmitting,
    toggleProduct,
  } = useOptOutPopupStore();

  useEffect(() => {
    if (data) {
      hydrate(data);
    }
  }, [data, hydrate]);

  if (isLoading && !popupData) {
    return <LoadingState />;
  }

  if (error && !popupData) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!popupData) {
    return (
      <ErrorState
        error="Popup data is unavailable."
        onRetry={refetch}
      />
    );
  }

  const popupSection = popupData.opt_out_popup_sections[0]?.data;

  if (popupData.layout_variant !== "opt_out_popup" || !popupSection) {
    return (
      <ErrorState
        error="Popup layout data is unavailable."
        onRetry={refetch}
      />
    );
  }

  const handleSubmit = async () => {
    const selectedProducts = getSelectedProducts();
    setSubmitError(null);
    setSubmitting(true);

    try {
      if (!selectedProducts.length) {
        window.location.href = appendOrderId(
          popupData.upsell_links.link_decline,
          window.orderId,
        );
        return;
      }

      for (const product of selectedProducts) {
        const isSuccessful = await postUpsell(
          buildUpsellPayload(product, window.orderId, window.postId),
        );

        if (!isSuccessful) {
          window.location.href = appendOrderId(
            popupData.upsell_links.link_failure,
            window.orderId,
          );
          return;
        }
      }

      window.location.href = appendOrderId(
        popupData.upsell_links.link_success,
        window.orderId,
      );
    } catch {
      setSubmitError("An unexpected error occurred. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="popup-overlay flex min-h-screen items-center justify-center px-2 py-2 sm:px-5"
        style={
          {
            "--bg-desktop": `url("${popupSection.overlay_bg_img}")`,
            "--bg-mobile": `url("${popupSection.overlay_bg_img_mob}")`,
            "--cta-bg": popupData.colors.primary_bg_color,
            "--cta-hover": popupData.colors.primary_bg_hover_color,
            "--cta-shadow": popupData.colors.primary_shadow_bg_color,
          } as React.CSSProperties
        }
      >
        <div className="w-full max-w-[524px] overflow-hidden rounded-[10px] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
          <header className="bg-[var(--cta-bg)] px-4 py-4 text-center sm:px-6 sm:py-6">
            <div
              className="text-2xl leading-6 font-bold text-white capitalize sm:text-[28px]"
              style={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {popupSection.tr_header_text}
            </div>
          </header>

          <div className="max-h-[calc(100svh-150px)] overflow-y-auto bg-white px-4 py-4 sm:max-h-[calc(100vh-200px)] sm:px-8 sm:py-8">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div
                className="text-center text-[18px] leading-7 font-black text-neutral-800 capitalize sm:text-[28px] sm:leading-10"
                dangerouslySetInnerHTML={{ __html: popupSection.tr_heading }}
                style={{ fontFamily: '"Roboto", sans-serif' }}
              />

              <div
                className={`flex flex-col gap-3${popupData.enable_scroll ? ' max-h-[219px] overflow-y-auto' : ''}`}
                style={popupData.enable_scroll ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties : undefined}
              >
                {popupData.offer_products.map((product) => (
                  <div key={product.id} className="border-t border-neutral-200 first:border-t">
                    <ProductCard
                      currencyCode={popupData.currency_code}
                      isSelected={selectedProductIds.includes(product.id)}
                      locale={popupData.locale}
                      onToggle={toggleProduct}
                      product={product}
                    />
                  </div>
                ))}
                <div className="border-t border-neutral-200" />
              </div>

              <div className="flex flex-col items-center gap-4">
                <button
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border-b-2 border-[var(--cta-shadow)] bg-[var(--cta-bg)] px-4 py-4 text-white transition hover:bg-[var(--cta-hover)] disabled:cursor-not-allowed disabled:opacity-60 sm:gap-4 sm:px-6 sm:py-[18px]"
                  disabled={isSubmitting}
                  onClick={() => {
                    void handleSubmit();
                  }}
                  type="button"
                >
                  <span
                    className="whitespace-nowrap text-base leading-6 font-bold uppercase text-white [text-shadow:0_2px_2px_rgba(0,0,0,0.25)] text-lg"
                    style={{ fontFamily: '"Poppins", sans-serif' }}
                  >
                    {popupSection.tr_cta_label}
                  </span>
                  <span className="flex h-4 w-4 items-center justify-center text-white">
                    <SubmitArrow />
                  </span>
                </button>

                {submitError ? (
                  <p className="text-center text-sm text-red-600">{submitError}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isSubmitting ? (
        <div className="fixed inset-0 z-[10000] grid place-items-center bg-black/70">
          <div className="h-14 w-14 rounded-full border-4 border-white/40 border-t-[var(--cta-bg)] animate-spin" />
        </div>
      ) : null}
    </>
  );
}
